import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback } from 'react';

import { useGeoAreaNameMap } from '@/features/api/hooks/shared-app/useGeoAreaNameMap';

/**
 * Returns a callback that maps an organization's `geoAreaIds` to a
 * comma-joined string of resolved geo area names. Returns `undefined`
 * when the organization has no geo areas or none of the ids could be
 * resolved to a name.
 *
 * @param organizationId - The creator organization id used to fetch the geo area tree
 */
export const useDescribeOrganizationGeoAreas = (organizationId?: string) => {
  const geoAreaNameById = useGeoAreaNameMap(organizationId);

  return useCallback(
    (organization: ClientOrganization) => {
      const ids = organization.geoAreaIds ?? [];
      if (ids.length === 0) return;
      const names = ids
        .map((id) => (id ? geoAreaNameById.get(id) : undefined))
        .filter((name): name is string => !!name);
      return names.length > 0 ? names.join(', ') : undefined;
    },
    [geoAreaNameById]
  );
};
